import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase, getAppUrl } from '../lib/supabase';
import type { Plan } from '../lib/entitlements';
import { track } from '../lib/analytics';
import { trackMetaEvent } from '../lib/meta-pixel';
import { clearCachedAssets } from '../lib/assetCache';

// ─── Post-auth intent (survives OAuth round-trips & email confirmation) ──────

const INTENT_KEY = 'anima-checkout-intent';
const INTENT_TTL = 30 * 60 * 1000; // 30 minutes

type PostAuthIntent =
  | { action: 'checkout'; plan: string; ts: number }
  | { action: 'editor'; ts: number };

export function setPostAuthIntent(intent: { action: 'checkout'; plan: string } | { action: 'editor' }) {
  localStorage.setItem(INTENT_KEY, JSON.stringify({ ...intent, ts: Date.now() }));
}

function consumePostAuthIntent(): PostAuthIntent | null {
  const raw = localStorage.getItem(INTENT_KEY);
  if (!raw) return null;
  localStorage.removeItem(INTENT_KEY);
  try {
    const parsed = JSON.parse(raw) as PostAuthIntent;
    if (Date.now() - parsed.ts > INTENT_TTL) return null;
    return parsed;
  } catch {
    return null;
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface Profile {
  id: string;
  email: string;
  plan: Plan;
  stripe_customer_id: string | null;
  created_at: string;
  current_period_end: string | null;
  subscription_id: string | null;
  billing_interval: 'monthly' | 'yearly' | null;
  cancel_at_period_end: boolean;
}

interface AuthModalOptions {
  defaultView?: 'sign-in' | 'sign-up';
}

interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  /** Current plan — defaults to 'free' when signed out or profile not yet loaded. */
  plan: Plan;
  /** True until the first onAuthStateChange callback fires. */
  authLoading: boolean;
  /** True when a session expired without user-initiated logout. */
  sessionExpired: boolean;
  clearSessionExpired: () => void;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<{ error: string | null }>;
  signUpWithEmail: (email: string, password: string) => Promise<{ error: string | null }>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
  openAuthModal: (opts?: AuthModalOptions) => void;
  closeAuthModal: () => void;
  authModalOpen: boolean;
  authModalDefaultView: 'sign-in' | 'sign-up';
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function fetchProfile(userId: string): Promise<Profile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) return null;
    return data as Profile;
  } catch {
    return null;
  }
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalDefaultView, setAuthModalDefaultView] = useState<'sign-in' | 'sign-up'>('sign-in');
  const [authLoading, setAuthLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const userRef = useRef<User | null>(null);
  const userInitiatedSignOut = useRef(false);

  const plan: Plan = profile?.plan ?? 'free';

  const openAuthModal = useCallback((opts?: AuthModalOptions) => {
    setAuthModalDefaultView(opts?.defaultView ?? 'sign-in');
    setAuthModalOpen(true);
  }, []);
  const closeAuthModal = useCallback(() => setAuthModalOpen(false), []);
  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  const refreshProfile = useCallback(async () => {
    try {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (u) {
        const p = await fetchProfile(u.id);
        setProfile(p);
      }
    } catch {
      // Supabase not configured or network error — stay at defaults
    }
  }, []);

  // Initial session + auth state listener
  const signInTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const u = session?.user ?? null;
        const prevUser = userRef.current;
        userRef.current = u;
        setUser(u);
        if (u && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED' || event === 'PASSWORD_RECOVERY')) {
          setSessionExpired(false);
          const p = await fetchProfile(u.id);
          setProfile(p);

          if (event === 'SIGNED_IN') {
            setAuthModalOpen(false);
            const method = u.app_metadata?.provider === 'google' ? 'google' : 'email';
            const sid = session?.access_token?.slice(-16) ?? u.id;
            if (signInTrackedRef.current !== sid) {
              signInTrackedRef.current = sid;
              track('sign_in', { method });
            }

            // Detect first email confirmation (confirmed within last 30s)
            if (method === 'email' && u.email_confirmed_at) {
              const confirmedAt = new Date(u.email_confirmed_at).getTime();
              if (Date.now() - confirmedAt < 30_000) {
                track('sign_up_email_confirmed');
                trackMetaEvent('CompleteRegistration');
              }
            }

            // Detect first OAuth signup (user record created within last 60s)
            if (method === 'google' && u.created_at) {
              const createdAt = new Date(u.created_at).getTime();
              if (Date.now() - createdAt < 60_000) {
                trackMetaEvent('CompleteRegistration');
              }
            }

            // Process post-auth intent (set from pricing page)
            const intent = consumePostAuthIntent();
            if (intent) {
              if (intent.action === 'editor') {
                // Free-plan signup: go straight to editor
                window.location.href = `${getAppUrl()}/editor`;
              } else if (intent.action === 'checkout') {
                const userPlan = p?.plan ?? 'free';
                // Guard: only create a checkout for genuinely new purchases.
                // Only free→pro is a valid path now (lifetime is no longer sold).
                const shouldCheckout = userPlan === 'free';
                if (shouldCheckout) {
                  try {
                    const { data: coData, error: coErr } = await supabase.functions.invoke('create-checkout', {
                      body: { plan: intent.plan, userId: u.id, returnUrl: `${getAppUrl()}/editor?checkout=success` },
                    });
                    if (!coErr && coData?.url) {
                      window.location.href = coData.url as string;
                    }
                  } catch {
                    // Checkout creation failed — fall through to normal flow
                  }
                }
              }
            }
          }
        } else if (event === 'SIGNED_OUT') {
          // Detect session expiry vs user-initiated logout
          if (!userInitiatedSignOut.current && prevUser) {
            setSessionExpired(true);
          }
          userInitiatedSignOut.current = false;
          setUser(null);
          setProfile(null);
        }
        setAuthLoading(false);
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Re-fetch profile on tab focus (catches plan changes made externally)
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') refreshProfile();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [refreshProfile]);

  // Refresh profile after returning from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      refreshProfile();
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      window.history.replaceState({}, '', url.toString());
    }
  }, [refreshProfile]);

  // PKCE code exchange — handles signup confirmation and email change links
  // (/reset page handles its own exchange in ResetPassword.tsx)
  useEffect(() => {
    if (window.location.pathname === '/reset') return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return;
    supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
      if (error) console.error('Code exchange failed:', error.message);
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      window.history.replaceState({}, '', url.toString());
    });
  }, []);

  const signIn = useCallback(async () => {
    try {
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${getAppUrl()}/editor` },
      });
    } catch (err) {
      console.error('Sign-in error:', err);
    }
  }, []);

  const signOut = useCallback(async () => {
    userInitiatedSignOut.current = true;
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore
    }
    await clearCachedAssets();
    localStorage.removeItem('anima-active-preset');
    localStorage.removeItem('anima-sidebar-tab');
    setUser(null);
    setProfile(null);
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${getAppUrl()}/editor` },
    });
    if (data.user?.identities?.length === 0) {
      return { error: 'This email is already linked to Google sign-in. Try continuing with Google instead.' };
    }
    return { error: error?.message ?? null };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${getAppUrl()}/reset`,
    });
    return { error: error?.message ?? null };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error?.message ?? null };
  }, []);

  return (
    <AuthContext.Provider value={{
      user, profile, plan, authLoading, sessionExpired, clearSessionExpired,
      signIn, signOut, refreshProfile,
      signInWithEmail, signUpWithEmail, resetPassword, updatePassword,
      openAuthModal, closeAuthModal, authModalOpen, authModalDefaultView,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
